/**
 * One intro page per audience.
 *
 * Glenn's Sept 22 2026 email: "unique access points - for scouts, fire
 * officials, community leaders and property owners, so that what they see is
 * clear and simple for that audience." Each audience gets its own link, its
 * own page and (Phase 2) its own email.
 *
 * Keyed by UserRole on purpose. The audience in the link is the same
 * identifier as the role the sign-up form pre-ticks, so there is one word
 * for the idea rather than two that can drift apart.
 *
 * Four of the nine roles are here. The other five describe capabilities that
 * do not exist yet, so they have no page until the client writes the copy
 * and the features land. An unknown slug falls back to 'fire-scout'.
 *
 * THE COPY IN THIS FILE IS A FIRST DRAFT. Glenn writes the real wording.
 * The page reads every field off these objects, so replacing the text is an
 * edit here and nothing else.
 */
import type { Href } from 'expo-router';
import {
  Eye,
  Camera,
  Users,
  Layers,
  ClipboardCheck,
  Mail,
  Newspaper,
  Home,
  Shield,
  type LucideIcon,
} from 'lucide-react-native';
import type { UserRole } from '../types';

/**
 * The plan the Community Leader page opens. Glenn's own "Sample Plan"
 * (rkysD9VryH8VYmiqheq5) is shared but empty since Sept 22, so the page
 * points at the seeded fixture instead, which has seven defenses and reads
 * correctly to a visitor. Switch this back once Glenn restores his.
 */
export const AUDIENCE_SAMPLE_PLAN_ID = 'audit-fixture-plan';

export interface AudiencePoint {
  icon: LucideIcon;
  title: string;
  body: string;
}

export interface AudienceCta {
  label: string;
  href: Href;
  /** Summon MapPathsOverlay once the map has mounted. */
  showMapPaths?: boolean;
  /** Carry ?property= and ?model= through to the map (the insurer links). */
  forwardPropertyParams?: boolean;
}

export interface Audience {
  id: UserRole;
  eyebrow: string;
  headline: string;
  lede: string;
  /** Exactly three. The page lays out for three and the test asserts it. */
  points: AudiencePoint[];
  cta: AudienceCta;
  /** Sentence under the Create an account link. */
  signUpNote: string;
}

export const AUDIENCES: Partial<Record<UserRole, Audience>> = {
  'fire-scout': {
    id: 'fire-scout',
    eyebrow: 'For scouts and volunteers',
    headline: 'Walk your neighborhood. Put what you see on the map.',
    lede: 'A phone is the whole kit. Photograph the work that has been done and the work that still needs doing, and your community can see both in one place.',
    points: [
      {
        icon: Camera,
        title: 'Post what you see',
        body: 'Photograph it, drop it on the map, add a line. Under a minute, standing on the sidewalk.',
      },
      {
        icon: Eye,
        title: 'Progress and opportunities',
        body: 'Two kinds of post. One shows work that is finished, the other shows work worth doing.',
      },
      {
        icon: Users,
        title: 'Nobody walks alone',
        body: 'Your posts sit next to everyone else’s, so a street reads as a street instead of nine separate errands.',
      },
    ],
    cta: { label: 'Open the map', href: '/(tabs)' as Href, showMapPaths: true },
    signUpNote: 'An account lets you post. Looking around needs nothing.',
  },

  'fire-official': {
    id: 'fire-official',
    eyebrow: 'For fire officials',
    headline: 'See what your community has actually done.',
    lede: 'The plans exist. What is usually missing is whether the work happened. REDI puts the plan and the evidence on the same map.',
    points: [
      {
        icon: Layers,
        title: 'Your existing plans, as layers',
        body: 'Pages of the community wildfire protection plan pinned to the real map, next to hazard zones and fire history.',
      },
      {
        icon: Eye,
        title: 'Observations from the ground',
        body: 'Dated, placed photographs from residents and volunteers, not a survey somebody filled in later.',
      },
      {
        icon: ClipboardCheck,
        title: 'Defenses to confirm',
        body: 'A drawn plan you can check against what you know, before anyone commits to it.',
      },
    ],
    cta: { label: 'See your area', href: '/(tabs)' as Href, showMapPaths: true },
    signUpNote: 'Browsing needs no account. You will want one to comment.',
  },

  'community-leader': {
    id: 'community-leader',
    eyebrow: 'For community leaders',
    headline: 'Turn a plan into work somebody has agreed to do.',
    lede: 'Draw the defense, name who is doing each part, then ask them how it is going. Nobody you chase has to make an account.',
    points: [
      {
        icon: Users,
        title: 'Name the parties',
        body: 'A fire safe council, the county, a contractor, a group of neighbors. Each attached to the part of the plan they own.',
      },
      {
        icon: Mail,
        title: 'Ask for an update',
        body: 'One email, one link, no account. They mark what is done and it lands back on the plan.',
      },
      {
        icon: Newspaper,
        title: 'Publish the progress',
        body: 'Roll the answers into a news item and send it back out to the people who did the work.',
      },
    ],
    cta: { label: 'See a defense plan', href: `/plan/${AUDIENCE_SAMPLE_PLAN_ID}` as Href },
    signUpNote: 'Drawing a plan needs an account. Reading one does not.',
  },

  'property-owner': {
    id: 'property-owner',
    eyebrow: 'For property owners',
    headline: 'Find your home and see what would lower its risk.',
    lede: 'A checklist built from the standard your insurer actually uses. Tick what is already done, photograph the rest as you finish it.',
    points: [
      {
        icon: Home,
        title: 'Claim your property',
        body: 'One address, one owner. The checklist and everything you record against it belong to you.',
      },
      {
        icon: ClipboardCheck,
        title: 'Work the list',
        body: 'Ten to thirty items, depending on whose standard you are working to. Photographs count as proof.',
      },
      {
        icon: Shield,
        title: 'Show your work',
        body: 'Send a readiness report to your agent, or keep it to yourself. You decide who sees what.',
      },
    ],
    cta: {
      label: 'Find my property',
      href: '/(tabs)' as Href,
      showMapPaths: true,
      forwardPropertyParams: true,
    },
    signUpNote: 'Looking needs no account. Saving your progress does.',
  },
};

/**
 * Short slugs, because Glenn writes these links by hand into email and
 * should not have to type 'property-owner'.
 */
export const AUDIENCE_ALIASES: Record<string, UserRole> = {
  scout: 'fire-scout',
  scouts: 'fire-scout',
  official: 'fire-official',
  officials: 'fire-official',
  leader: 'community-leader',
  leaders: 'community-leader',
  owner: 'property-owner',
  owners: 'property-owner',
};

/** The audience an unknown or missing slug lands on. */
export const DEFAULT_AUDIENCE: UserRole = 'fire-scout';

/**
 * Resolve a URL segment to an audience. Returns undefined for anything that
 * is not an audience; the screen decides what to do with that (it falls back
 * to DEFAULT_AUDIENCE rather than 404ing, because these links go out in mail
 * somebody typed by hand).
 */
export function audienceBySlug(slug: string | undefined): Audience | undefined {
  if (!slug) return undefined;
  const key = slug.trim().toLowerCase();
  const direct = AUDIENCES[key as UserRole];
  if (direct) return direct;
  const aliased = AUDIENCE_ALIASES[key];
  return aliased ? AUDIENCES[aliased] : undefined;
}

/** Every audience with a page, in the order the invite picker shows them. */
export const AUDIENCE_IDS: UserRole[] = [
  'fire-scout',
  'property-owner',
  'fire-official',
  'community-leader',
];
